import React, { useState } from 'react';
import { reportService } from '../../services/reportService';
import { billingService } from '../../services/billingService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  WaterDrop as WaterIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const UsageAnomalyPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState('All');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);

  const handleSearch = async () => {
    setError('');
    setLoading(true);
    try {
      const { success, data, error: serviceError } = await reportService.getUsageAnomalies(10, paymentStatus);
      if (!success) {
        setError(serviceError || 'Failed to load usage anomalies.');
        setResults([]);
        return;
      }
      setResults(data || []);
    } catch (e) {
      setError(e.message || 'Failed to load usage anomalies.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatNumber = (value) => {
    if (value == null || isNaN(value)) return '';
    return Number(value).toFixed(2);
  };

  const handleRowClick = async (row) => {
    setSelectedAnomaly(row);
    setHistoryOpen(true);
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const result = await billingService.getCustomerBills(row.customerid);
      if (!result.success) {
        setHistoryError(result.error || 'Failed to load customer history.');
        setHistoryData([]);
        return;
      }

      const allBills = result.data || [];
      const filteredBills = paymentStatus === 'All'
        ? allBills
        : allBills.filter(bill => bill.paymentstatus === paymentStatus);

      const bills = filteredBills
        .filter(bill => bill.consumption != null)
        .map(bill => ({
          billedmonth: bill.billedmonth,
          consumption: parseFloat(bill.consumption || 0),
          basicamount: parseFloat(bill.basicamount || 0)
        }))
        .sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));
      setHistoryData(bills);
    } catch (e) {
      setHistoryError(e.message || 'Failed to load customer history.');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getMaxConsumption = () => {
    if (!historyData || historyData.length === 0) return 0;
    return historyData.reduce((max, item) => (item.consumption > max ? item.consumption : max), 0);
  };

  const isAnomalyMonth = (month) => {
    if (!selectedAnomaly) return false;
    return month === selectedAnomaly.month;
  };

  const openBillingForMonth = (month) => {
    if (!selectedAnomaly || !selectedAnomaly.customerid || !month) return;

    const query = `customerid=${encodeURIComponent(
      selectedAnomaly.customerid
    )}&month=${encodeURIComponent(month)}`;

    const base = `${window.location.origin}${window.location.pathname}`;
    const url = `${base}#/portal/water-billing/billing?${query}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const maxConsumption = getMaxConsumption();

  const handlePrint = () => {
    if (!results || results.length === 0) {
      return;
    }

    const title = 'Usage Anomalies Report';
    const statusLabel = paymentStatus === 'All' ? 'All' : paymentStatus;
    const generatedAt = new Date().toLocaleString('en-PH');

    const tableRows = results
      .map((row, index) => {
        return `
          <tr>
            <td>${index + 1}</td>
            <td>${row.customerid}</td>
            <td>${row.name || ''}</td>
            <td>${row.barangay || ''}</td>
            <td>${row.type || ''}</td>
            <td>${formatMonth(row.month) || ''}</td>
            <td style="text-align: right;">${formatNumber(row.currentConsumption)}</td>
            <td style="text-align: right;">${formatNumber(row.averageHistoryConsumption)}</td>
            <td style="text-align: right;">${formatNumber(row.difference)}</td>
          </tr>
        `;
      })
      .join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; margin-bottom: 4px; }
            h2 { text-align: center; margin-top: 0; font-size: 14px; color: #555; }
            .meta { margin: 10px 0 20px 0; font-size: 12px; color: #444; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 6px 8px; }
            th { background-color: #f3f4f6; text-align: left; }
            .no-print { margin-top: 20px; text-align: center; }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <h2>Payment Status: ${statusLabel}</h2>
          <div class="meta">
            Generated: ${generatedAt}<br/>
            Total anomalies: ${results.length.toLocaleString()}
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Customer ID</th>
                <th>Name</th>
                <th>Barangay</th>
                <th>Type</th>
                <th>Month</th>
                <th>Current Usage (m³)</th>
                <th>Average History (m³)</th>
                <th>Difference (m³)</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="no-print">
            <button onclick="window.print()" style="padding: 8px 16px; margin-right: 8px;">Print</button>
            <button onclick="window.close()" style="padding: 8px 16px;">Close</button>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExportExcel = async () => {
    if (!results || results.length === 0) {
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Usage Anomalies');

    worksheet.pageSetup.orientation = 'landscape';

    const statusLabel = paymentStatus === 'All' ? 'All' : paymentStatus;

    worksheet.mergeCells('A1:I1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'Usage Anomalies Report';
    titleCell.font = { name: 'Calibri', size: 14, bold: true };
    titleCell.alignment = { horizontal: 'center' };

    worksheet.mergeCells('A2:I2');
    const subTitleCell = worksheet.getCell('A2');
    subTitleCell.value = `Payment Status: ${statusLabel} • Generated: ${new Date().toLocaleString('en-PH')}`;
    subTitleCell.font = { name: 'Calibri', size: 10 };
    subTitleCell.alignment = { horizontal: 'center' };

    worksheet.addRow([]);

    worksheet.addRow([
      '#',
      'Customer ID',
      'Name',
      'Barangay',
      'Type',
      'Month',
      'Current Usage (m³)',
      'Average History (m³)',
      'Difference (m³)'
    ]);

    const headerRow = worksheet.getRow(4);
    headerRow.font = { name: 'Calibri', size: 11, bold: true };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.eachCell(cell => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' }
      };
    });

    results.forEach((row, index) => {
      const sheetRow = worksheet.addRow([
        index + 1,
        row.customerid,
        row.name || '',
        row.barangay || '',
        row.type || '',
        formatMonth(row.month) || '',
        row.currentConsumption,
        row.averageHistoryConsumption,
        row.difference
      ]);

      sheetRow.getCell(1).alignment = { horizontal: 'center' };
      sheetRow.getCell(7).numFmt = '0.00';
      sheetRow.getCell(8).numFmt = '0.00';
      sheetRow.getCell(9).numFmt = '0.00';

      sheetRow.eachCell(cell => {
        cell.font = { name: 'Calibri', size: 10 };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    worksheet.getColumn(1).width = 4;
    worksheet.getColumn(2).width = 14;
    worksheet.getColumn(3).width = 26;
    worksheet.getColumn(4).width = 18;
    worksheet.getColumn(5).width = 12;
    worksheet.getColumn(6).width = 16;
    worksheet.getColumn(7).width = 18;
    worksheet.getColumn(8).width = 20;
    worksheet.getColumn(9).width = 16;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const fileStatus = paymentStatus === 'All' ? 'All' : paymentStatus;
    const filename = `usage_anomalies_${fileStatus}_${new Date().getTime()}.xlsx`;
    saveAs(blob, filename);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="xl" sx={{ p: 0 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WaterIcon color="primary" /> Usage Anomalies
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }} variant="outlined">
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  This will scan billing records and flag months where a customer&apos;s usage is at least 10 cubic meters higher than their own average historical usage.
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="payment-status-label">Payment Status</InputLabel>
                  <Select
                    labelId="payment-status-label"
                    label="Payment Status"
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Unpaid">Unpaid</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    Analyze Usage
                  </Button>
                  {results.length > 0 && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                      >
                        Print
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                      >
                        Export
                      </Button>
                    </>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" /> Unusual Usage Results
              </Typography>
              {results.length > 0 && (
                <Chip
                  label={`Total anomalies: ${results.length}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : results.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unusual usage found in billing history.
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Month</TableCell>
                      <TableCell>Barangay</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Current Usage</TableCell>
                      <TableCell align="right">Average History</TableCell>
                      <TableCell align="right">Difference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((row, index) => (
                      <TableRow
                        key={`${row.customerid}-${row.month}-${index}`}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(row)}
                      >
                        <TableCell>{row.customerid}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{formatMonth(row.month)}</TableCell>
                        <TableCell>{row.barangay}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell align="right">{formatNumber(row.currentConsumption)}</TableCell>
                        <TableCell align="right">{formatNumber(row.averageHistoryConsumption)}</TableCell>
                        <TableCell align="right">{formatNumber(row.difference)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>

      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Usage History
            </Typography>
            {selectedAnomaly && (
              <Typography variant="body2" color="text.secondary">
                {selectedAnomaly.customerid} &mdash; {selectedAnomaly.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setHistoryOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {historyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {historyError}
            </Alert>
          )}

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyData.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No billing history available for this customer.
            </Typography>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Monthly consumption history
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Consumption (m³)</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.map((item) => {
                      const highlighted = isAnomalyMonth(item.billedmonth);
                      return (
                        <TableRow
                          key={item.billedmonth}
                          selected={highlighted}
                          hover={highlighted}
                          sx={highlighted ? { cursor: 'pointer' } : undefined}
                          onClick={
                            highlighted ? () => openBillingForMonth(item.billedmonth) : undefined
                          }
                        >
                          <TableCell sx={highlighted ? { textDecoration: 'underline' } : undefined}>
                            {formatMonth(item.billedmonth)}
                          </TableCell>
                          <TableCell align="right">{formatNumber(item.consumption)}</TableCell>
                          <TableCell align="right">
                            ₱{parseFloat(item.basicamount || 0).toLocaleString('en-PH', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Consumption graph
              </Typography>
              <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 1 }}>
                {historyData.map((item) => {
                  const ratio = maxConsumption > 0 ? item.consumption / maxConsumption : 0;
                  const widthPercent = Math.max(ratio * 100, 4);
                  const highlighted = isAnomalyMonth(item.billedmonth);
                  return (
                    <Box
                      key={`bar-${item.billedmonth}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        cursor: highlighted ? 'pointer' : 'default'
                      }}
                      onClick={
                        highlighted ? () => openBillingForMonth(item.billedmonth) : undefined
                      }
                    >
                      <Box sx={{ width: 140, pr: 1 }}>
                        <Typography
                          variant="caption"
                          sx={highlighted ? { textDecoration: 'underline' } : undefined}
                        >
                          {formatMonth(item.billedmonth)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flexGrow: 1,
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          overflow: 'hidden',
                          mr: 1
                        }}
                      >
                        <Box
                          sx={{
                            width: `${widthPercent}%`,
                            bgcolor: highlighted ? 'warning.main' : 'primary.main',
                            height: 20
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 60 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: highlighted ? 'bold' : 'normal' }}
                        >
                          {formatNumber(item.consumption)} m³
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsageAnomalyPage;
